import React, { useState, useEffect } from 'react';
import SurveyForm from '../forms/SurveyForm';
import feedbackSurveySchema from './feedbackSurveySchema';

/**
 * Feedback Side Panel - Always visible feedback button that opens a side panel
 * Simple, no Bootstrap modal BS, just clean CSS transitions
 * Animates through different states to encourage feedback
 */
const FeedbackSidePanel = ({
  onSubmit = null,
  sessionData = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if feedback was submitted this session (persisted in localStorage)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(() => {
    const sessionFeedback = localStorage.getItem('vcx_session_feedback_submitted');
    return sessionFeedback === 'true';
  });

  const [buttonState, setButtonState] = useState(0); // 0-3 before feedback, 0-1 after

  const {
    eventsReviewedCount = 0,
    sessionDurationMinutes = 0,
    timeOfDay = 'N/A'
  } = sessionData;

  // Cycle through button states every 5-10 minutes
  useEffect(() => {
    const randomInterval = () => Math.floor(Math.random() * (10 - 5 + 1) + 5) * 60 * 1000 ;

    const cycleState = () => {
      setButtonState(prev => {
        if (feedbackSubmitted) {
          // After feedback: toggle between 0 (full) and 1 (condensed)
          return prev === 0 ? 1 : 1;
        } else {
          // Before feedback: cycle through 0-3
          return (prev + 1) % 4;
        }
      });
    };

    const timer = setTimeout(() => {
      cycleState();
      // Set next cycle
      const nextTimer = setInterval(cycleState, randomInterval());
      return () => clearInterval(nextTimer);
    }, randomInterval());

    return () => clearTimeout(timer);
  }, [feedbackSubmitted]);

  const handleSubmit = async (surveyData) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(surveyData);
      }
      // Mark feedback as submitted for this session
      localStorage.setItem('vcx_session_feedback_submitted', 'true');
      setFeedbackSubmitted(true);
      setButtonState(0); // Reset to first "thank you" state
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    setIsOpen(false);
  };

  // Get button configuration based on current state
  const getButtonConfig = () => {
    if (feedbackSubmitted) {
      // After feedback: 2 states
      if (buttonState === 0) {
        // Full green with thank you message
        return {
          className: 'btn btn-success feedback-btn-base feedback-btn-thanks shadow',
          content: (
            <>
              <i className="fas fa-check-circle"></i>
              Thanks for your feedback!
            </>
          )
        };
      } else {
        // Condensed green with pulsing heart
        return {
          className: 'btn btn-success feedback-btn-base feedback-btn-heart shadow',
          content: <i className="fas fa-heart feedback-heart-pulse"></i>
        };
      }
    } else {
      // Before feedback: 4 states
      switch (buttonState) {
        case 0:
          // Basic blue with "Feedback"
          return {
            className: 'btn btn-primary feedback-btn-base feedback-btn-basic shadow',
            content: (
              <>
                <i className={`fas ${isOpen ? 'fa-times' : 'fa-comment-dots'}`}></i>
                {isOpen ? 'Close' : 'Feedback'}
              </>
            )
          };
        case 1:
          // Condensed blue with thumbs
          return {
            className: 'btn btn-primary feedback-btn-base feedback-btn-condensed shadow',
            content: (
              <>
                <i className="fas fa-thumbs-up"></i>
                <i className="fas fa-thumbs-down"></i>
              </>
            )
          };
        case 2:
          // Full size orange/white with glowing border
          return {
            className: 'feedback-btn-base feedback-btn-glow shadow',
            content: (
              <>
                <i className={`fas ${isOpen ? 'fa-times' : 'fa-star'}`}></i>
                {isOpen ? 'Close' : 'Share Your Thoughts'}
              </>
            )
          };
        case 3:
          // Gray thumbs only
          return {
            className: 'btn btn-secondary feedback-btn-base feedback-btn-gray shadow',
            content: (
              <>
                <i className="fas fa-thumbs-up"></i>
                <i className="fas fa-thumbs-down"></i>
              </>
            )
          };
        default:
          return {
            className: 'btn btn-primary feedback-btn-base feedback-btn-basic shadow',
            content: (
              <>
                <i className="fas fa-comment-dots"></i>
                Feedback
              </>
            )
          };
      }
    }
  };

  const buttonConfig = getButtonConfig();

  // Get last feedback submission for prefilling
  const getLastFeedback = () => {
    try {
      const history = JSON.parse(localStorage.getItem('vcx_feedback_history') || '[]');
      if (history.length > 0) {
        // Return last submission, excluding metadata fields
        const lastSubmission = history[history.length - 1];
        const { submitted_at, event_id, user_id, events_reviewed_count, session_duration_minutes, time_of_day, ...feedbackData } = lastSubmission;
        return feedbackData;
      }
    } catch (error) {
      console.error('Error loading previous feedback:', error);
    }
    return null;
  };

  // Enhanced schema with session metadata
  const enhancedSchema = React.useMemo(() => {
    return {
      ...feedbackSurveySchema,
      pages: [
        ...feedbackSurveySchema.pages,
        {
          name: "metadata",
          visible: false,
          elements: [
            { type: "text", name: "events_reviewed_count", defaultValue: eventsReviewedCount, visible: false },
            { type: "text", name: "session_duration_minutes", defaultValue: sessionDurationMinutes, visible: false },
            { type: "text", name: "time_of_day", defaultValue: timeOfDay, visible: false },
            { type: "text", name: "submitted_at", defaultValue: new Date().toISOString(), visible: false }
          ]
        }
      ]
    };
  }, [eventsReviewedCount, sessionDurationMinutes, timeOfDay]);

  return (
    <>
      {/* Floating Feedback Button - Always visible - Animates through states */}
      <button
        className={buttonConfig.className}
        onClick={() => setIsOpen(!isOpen)}
        title="Give feedback on your session"
      >
        {buttonConfig.content}
      </button>

      {/* Side Panel Overlay */}
      {isOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 1050,
            transition: 'opacity 0.3s ease'
          }}
          onClick={handleSkip}
        />
      )}

      {/* Side Panel - Only render when open */}
      {isOpen && (
        <div
          className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-flex flex-column"
          style={{
            width: '600px',
            maxWidth: '90vw',
            zIndex: 1051,
            overflowY: 'auto'
          }}
        >
          {/* Header */}
          <div className="border-bottom p-3 d-flex justify-content-between align-items-center sticky-top bg-white">
            <h5 className="mb-0">
              <i className="fas fa-comment-dots me-2 text-primary"></i>
              Session Feedback
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleSkip}
              aria-label="Close"
            ></button>
          </div>

          {/* Body */}
          <div className="flex-grow-1 p-3 position-relative">
            {/* Session Stats Banner */}
            {(eventsReviewedCount > 0 || sessionDurationMinutes > 0) && (
              <div className="alert alert-info d-flex align-items-center mb-4">
                <i className="fas fa-info-circle me-2"></i>
                <div className="small">
                  <strong>This session:</strong>{' '}
                  {eventsReviewedCount > 0 && (
                    <span className="me-3">
                      <i className="fas fa-list me-1"></i>
                      {eventsReviewedCount} {eventsReviewedCount === 1 ? 'event' : 'events'} reviewed
                    </span>
                  )}
                  {sessionDurationMinutes > 0 && (
                    <span>
                      <i className="fas fa-clock me-1"></i>
                      {sessionDurationMinutes} {sessionDurationMinutes === 1 ? 'minute' : 'minutes'} active
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Survey Form */}
            <SurveyForm
              json={enhancedSchema}
              onSubmit={handleSubmit}
              initialData={getLastFeedback()}
            />

            {/* Loading overlay */}
            {isSubmitting && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 1000
                }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Submitting...</span>
                  </div>
                  <p className="text-muted">Submitting your feedback...</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-top p-3 d-flex justify-content-between align-items-center bg-light">
            <button
              type="button"
              className="btn btn-link text-muted"
              onClick={handleSkip}
              disabled={isSubmitting}
            >
              Skip for now
            </button>
            <small className="text-muted">
              Your feedback helps us improve the platform
            </small>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackSidePanel;
